import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJoinHabit } from "@/shared/hooks";
import { useWallet } from "@/shared/hooks/useWallet";
import { Button } from "@/shared/ui/Button";
import { JoinPayModal, type JoinPayModalMode } from "@/shared/ui/JoinPayModal";
import { TopUpModal } from "@/shared/ui/TopUpModal";
import { hapticImpact, hapticNotify, showAlert } from "@/shared/telegram/tma";
import type { Habit } from "@/shared/types";

interface JoinButtonProps {
  habit: Habit;
  /**
   * Если уже известно, что юзер состоит в клубе (MarketplacePage знает это
   * по myHabits), кнопку отключаем.
   */
  isAlreadyJoined?: boolean;
}

/**
 * Flow Point: кнопка вступления в клуб.
 *
 * Режим определяется по кошельку (`GET /me/wallet`):
 * - `rejoin_clubs` содержит клуб → подписка живая, клуб покинут →
 *   вступление БЕСПЛАТНОЕ (сервер восстановит членство, период уже есть);
 * - `active_clubs` + живая подписка + status=active → «Ты уже в клубе»;
 * - `active_clubs` + живая подписка + status=paused → оплата подписки не
 *   нужна, нужно только докупить баллы (пауза снимается пополнением);
 * - `active_clubs` + истёкшая подписка → оплата нового периода
 *   (режим «resubscribe»);
 * - членства нет → обычное вступление с оплатой (режим «full»).
 *
 * Суммы в модалке считает сервер (`GET /payments/quote`) — клиент ничего не
 * придумывает.
 */
export function JoinButton({ habit, isAlreadyJoined = false }: JoinButtonProps) {
  const navigate = useNavigate();
  const { data: wallet, isLoading: isWalletLoading } = useWallet();
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const join = useJoinHabit();

  // subscription_until сравниваем строкой "YYYY-MM-DD" — ISO date сортируется
  // лексикографически так же, как хронологически.
  const walletClub = wallet?.active_clubs.find((c) => c.habit_id === habit.id);
  const rejoinClub = wallet?.rejoin_clubs?.find((c) => c.habit_id === habit.id);
  const today = new Date().toISOString().slice(0, 10);
  const hasActiveSubscription = !!(
    walletClub?.subscription_until && walletClub.subscription_until >= today
  );

  const mode: JoinPayModalMode =
    walletClub && !hasActiveSubscription ? "resubscribe" : "full";

  // ACTIVE + живая подписка → пользователь уже в клубе.
  const isActiveMember = walletClub?.status === "active" && hasActiveSubscription;
  // PAUSED + живая подписка → оплата не нужна, нужно докупить баллы.
  const isPaused = walletClub?.status === "paused" && hasActiveSubscription;
  const canRejoin = !!rejoinClub;
  const isJoined = isAlreadyJoined || isActiveMember;

  const handleClick = () => {
    // Пока кошелёк не загрузился — не действуем: иначе предложим оплату там,
    // где она не нужна (или наоборот).
    if (isWalletLoading || wallet === undefined) return;
    hapticImpact("medium");
    if (canRejoin) {
      join.mutate(habit.id, {
        onSuccess: () => {
          hapticNotify("success");
          void showAlert("Ты снова в клубе! Оплата не потребовалась.");
          navigate(`/habits/${habit.id}/today`);
        },
        onError: () => {
          hapticNotify("error");
          void showAlert("Не удалось вернуться в клуб. Попробуй ещё раз.");
        },
      });
      return;
    }
    if (isPaused) {
      setTopUpOpen(true);
      return;
    }
    setPayModalOpen(true);
  };

  const handleSuccess = () => {
    // onSuccess из JoinPayModal (после успешной оплаты) → navigate.
    // useJoinAndPay внутри JoinPayModal уже инвалидирует все нужные queries.
    navigate(`/habits/${habit.id}/today`);
  };

  const buttonText = canRejoin
    ? "Вступить бесплатно"
    : isJoined
    ? "Ты уже в клубе"
    : isPaused
    ? "Докупить баллы"
    : mode === "resubscribe"
    ? "Продлить подписку"
    : "Вступить";

  return (
    <>
      <Button
        onClick={handleClick}
        // loading prop показывает спиннер и блокирует клик. Текст «Вступить»
        // не меняется — это UX-паттерн «загрузка данных перед действием».
        loading={isWalletLoading}
        disabled={
          isJoined ||
          isWalletLoading ||
          wallet === undefined ||
          join.isPending
        }
        className="w-full"
        variant="primary"
      >
        {buttonText}
      </Button>

      {isPaused && (
        <TopUpModal
          open={topUpOpen}
          onClose={() => setTopUpOpen(false)}
          defaultAmount={Math.max(0, habit.penalty_amount - (wallet?.balance_fp ?? 0))}
        />
      )}

      {!isJoined && !isPaused && !canRejoin && (
        <JoinPayModal
          open={payModalOpen}
          onClose={() => setPayModalOpen(false)}
          onSuccess={handleSuccess}
          habit={{
            id: habit.id,
            title: habit.title,
            penalty_amount: habit.penalty_amount,
            price_month: habit.price_month,
          }}
          mode={mode}
        />
      )}
    </>
  );
}
